/**
 * The app's primary navigation, and the thing that fixes an information-
 * architecture mistake: Items and Categories were living under the month bar,
 * which quietly claimed they were month-scoped. They are not. Your list of
 * categories is not a fact about September.
 *
 * So the app has two levels. This one picks a SECTION — a different kind of
 * thing to be looking at — and there are only two, because there are only two
 * kinds: the money you are recording, and the lists that recording draws on.
 * Categories, Items and Payment methods are all the second kind: things you
 * set up once and then rarely touch, which is what Settings means. Shopping is
 * neither — it is the one screen about what has NOT happened yet — so it gets
 * its own place rather than being filed under either. Inside each
 * section a second row picks the view, and in Expenses a month bar scopes it.
 *
 * One component, two shapes: a rail down the left on a desktop, a bar across
 * the bottom on a phone, where the top of a large screen is out of thumb reach.
 * Same markup, same order, so nothing has to be learned twice.
 */
import type { ReactNode } from 'react';
import type { DevIdentities, Identity } from './api.js';

export type Section = 'expenses' | 'shopping' | 'analytics' | 'settings' | 'household' | 'profile';

interface Props {
  section: Section;
  onSection: (next: Section) => void;
  /** The URL this destination lives at, so the nav is made of real links. */
  hrefOf: (next: Section) => string;
  identity: Identity;
  /** Icons only, no labels. Desktop only — the phone bar has its own shape. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  identities: DevIdentities | null;
  onIdentity: (next: Identity) => void;
}

const NAV: Array<{ id: Section; label: string; hint: string; icon: ReactNode }> = [
  {
    id: 'expenses',
    label: 'Expenses',
    hint: 'Add, journal and budget — scoped to one month',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M5 3.5h14v17l-2.3-1.6-2.35 1.6-2.35-1.6-2.35 1.6L7.3 18.9 5 20.5z" strokeLinejoin="round" />
        <path d="M8.5 8.5h7M8.5 12.5h7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'shopping',
    label: 'Shopping',
    hint: 'Lists of what you mean to buy — the only screen here about the future',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M3.2 4.5h2.2l2.1 9.8a1.8 1.8 0 0 0 1.8 1.4h7.4a1.8 1.8 0 0 0 1.75-1.35L20.2 8.2H6.1" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9.6" cy="19.4" r="1.3" />
        <circle cx="17.1" cy="19.4" r="1.3" />
      </svg>
    ),
  },
  {
    id: 'analytics',
    label: 'Analytics',
    hint: 'What the ledger adds up to over a range you choose',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M4 20V4" strokeLinecap="round" />
        <path d="M4 20h16" strokeLinecap="round" />
        <path d="M7.5 15.2 11 10.8l3.2 2.6L20 6.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    hint: 'Categories, items and payment methods — the lists the Add screen draws on',
    icon: (
      // A gear is right here in a way it was not on Payment methods alone: this
      // really is the drawer where the app's lists are set up.
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <circle cx="12" cy="12" r="3.1" />
        <path d="M19.4 15a1.6 1.6 0 0 0 .32 1.77l.06.06a1.9 1.9 0 1 1-2.7 2.7l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-1 1.47v.17a1.9 1.9 0 1 1-3.8 0v-.09a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a1.9 1.9 0 1 1-2.7-2.7l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-1H3.5a1.9 1.9 0 1 1 0-3.8h.09a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a1.9 1.9 0 1 1 2.7-2.7l.06.06a1.6 1.6 0 0 0 1.77.32h.08a1.6 1.6 0 0 0 1-1.47V3.5a1.9 1.9 0 1 1 3.8 0v.09a1.6 1.6 0 0 0 1 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a1.9 1.9 0 1 1 2.7 2.7l-.06.06a1.6 1.6 0 0 0-.32 1.77v.08a1.6 1.6 0 0 0 1.47 1h.17a1.9 1.9 0 1 1 0 3.8h-.09a1.6 1.6 0 0 0-1.47 1z" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export function SideNav({
  section,
  onSection,
  hrefOf,
  identity,
  identities,
  onIdentity,
  collapsed,
  onToggleCollapsed,
}: Props) {
  return (
    <nav className={collapsed ? 'sidenav is-collapsed' : 'sidenav'} aria-label="Sections">
      <div className="sidenav-brand">
        <span className="sidenav-mark" aria-hidden="true">
          €
        </span>
        <span className="sidenav-title">Excent</span>
        {/* The toggle rides in the brand row rather than getting a row of its
            own: collapsed, the mark and this button are the only two things
            left up here, and they fit side by side at 60px. `aria-expanded`
            because this discloses the labels rather than navigating. */}
        <button
          type="button"
          className="sidenav-toggle"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
          title={collapsed ? 'Expand the menu' : 'Collapse the menu'}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
            <path d="M9.5 4.5v15" />
            {/* The chevron points where pressing it will send the panel. */}
            <path
              d={collapsed ? 'M13.6 9.4 16.2 12l-2.6 2.6' : 'M16.4 9.4 13.8 12l2.6 2.6'}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <ul className="sidenav-list">
        {NAV.map((item) => (
          <li key={item.id}>
            {/* An anchor with a real href, not a button. Middle-click,
                cmd-click and "copy link address" all work for free, and a
                screen reader announces a link to somewhere rather than a
                control that does something. The click handler navigates
                in-app; the href is what makes it a link, and the modifier
                check below is what leaves the browser's own behaviour
                alone. */}
            <a
              href={hrefOf(item.id)}
              className={section === item.id ? 'sidenav-item on' : 'sidenav-item'}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onSection(item.id);
              }}
              aria-current={section === item.id ? 'page' : undefined}
              // Collapsed there is no label on screen, so the tooltip becomes the
              // name; expanded the name is already visible and the hint adds something.
              title={collapsed ? item.label : item.hint}
            >
              {item.icon}
              <span className="sidenav-label">{item.label}</span>
            </a>
          </li>
        ))}
      </ul>

      {/* Only the DEV picker lives down here now. Who you are, which household
          you are in and the way out all moved to the avatar in the top-right
          corner — one target, always in the same place, instead of three
          controls competing for the corner you look at least.
          This stays because under AUTH_MODE=dev-headers there is no account,
          so no avatar renders, and without it there would be no way to change
          who you are pretending to be. `identities` is null in session mode,
          which is exactly when it should not appear. */}
      {identities && (
        <div className="sidenav-foot">
          <label className="sidenav-who">
            <span className="field-label">Acting as</span>
            <select
              value={`${identity.householdId}|${identity.memberId}`}
              onChange={(e) => {
                const [householdId = '', memberId = ''] = e.target.value.split('|');
                onIdentity({ householdId, memberId });
              }}
              aria-label="Acting as"
            >
              {identities.households.map((h) =>
                h.members.map((m) => (
                  <option key={m.memberId} value={`${h.householdId}|${m.memberId}`}>
                    {m.name} · {h.name}
                  </option>
                )),
              )}
            </select>
          </label>
          <p className="dev-note">Development headers, not a session.</p>
        </div>
      )}
    </nav>
  );
}
